import React, { useState, useEffect } from 'react';
import { 
  Truck,
  Tag,
  Clock,
  DollarSign,
  Loader2,
  RefreshCw,
  ChevronRight,
  Check,
  X,
  AlertCircle,
  Percent
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export default function SupplierDealsWidget({ showHeader = true }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const data = await apiRequest('/sales/supplier-deals');
      setDeals(data.deals || []);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
      setDeals(getMockDeals());
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new': return <Badge className="bg-blue-100 text-blue-700">New</Badge>;
      case 'reviewing': return <Badge className="bg-yellow-100 text-yellow-700">Reviewing</Badge>;
      case 'accepted': return <Badge className="bg-green-100 text-green-700">Accepted</Badge>;
      case 'expired': return <Badge className="bg-slate-100 text-slate-500">Expired</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getUrgencyColor = (days) => {
    if (days <= 2) return 'text-red-600 bg-red-50';
    if (days <= 5) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const newDeals = deals.filter(d => d.status === 'new').length;
  const totalValue = deals.reduce((acc, d) => acc + (d.potential_value || 0), 0);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="supplier-deals-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Truck size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Supplier Deals</h3>
              <p className="text-xs text-slate-500">{newDeals} new offers pending</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchDeals}>
            <RefreshCw size={16} />
          </Button>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 p-4 border-b border-slate-100">
        <div className="text-center p-3 bg-violet-50 rounded-lg">
          <p className="text-2xl font-bold text-violet-600">{deals.length}</p>
          <p className="text-xs text-slate-500">Active Offers</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">${(totalValue / 1000).toFixed(1)}k</p>
          <p className="text-xs text-slate-500">Potential Value</p>
        </div>
      </div>

      {/* Deals List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {deals.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No active supplier deals</p>
        ) : (
          deals.map((deal, idx) => (
            <div key={idx} className="p-3 rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-slate-900">{deal.supplier}</h4>
                    {getStatusBadge(deal.status)}
                  </div>
                  <p className="text-sm text-slate-600">{deal.product}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1 text-violet-600 font-medium">
                  <Percent size={14} />
                  {deal.discount}% off
                </span>
                <span className="flex items-center gap-1 text-green-600">
                  <DollarSign size={14} />
                  ${deal.potential_value?.toLocaleString()}
                </span>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getUrgencyColor(deal.days_left)}`}>
                  <Clock size={12} />
                  {deal.days_left}d left
                </span>
              </div>

              {deal.notes && (
                <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
                  {deal.notes}
                </p>
              )}

              {deal.status === 'new' && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="flex-1 h-8 bg-green-600 hover:bg-green-700">
                    <Check size={14} className="mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 h-8">
                    <X size={14} className="mr-1" /> Pass
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function getMockDeals() {
  return [
    {
      supplier: 'Haldirams',
      product: 'Namkeen Range (20+ SKUs)',
      discount: 25,
      potential_value: 15000,
      days_left: 3,
      status: 'new',
      notes: 'Bulk order minimum 500 units per SKU. Free shipping included.'
    },
    {
      supplier: 'Amul',
      product: 'Butter & Cheese Bundle',
      discount: 18,
      potential_value: 8500,
      days_left: 7,
      status: 'new',
      notes: 'Valid for orders placed by month end.'
    },
    {
      supplier: 'MDH Spices',
      product: 'Festival Pack Masalas',
      discount: 20,
      potential_value: 6200,
      days_left: 5,
      status: 'reviewing',
      notes: 'Special Holi/Diwali packaging available.'
    },
    {
      supplier: 'Patanjali',
      product: 'Ayurvedic Products',
      discount: 15,
      potential_value: 4800,
      days_left: 10,
      status: 'accepted',
      notes: 'Order placed. Delivery expected in 5 days.'
    },
    {
      supplier: 'Britannia',
      product: 'Biscuit Varieties',
      discount: 12,
      potential_value: 3500,
      days_left: 2,
      status: 'new',
      notes: 'Flash deal - expires soon!'
    }
  ];
}
